import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectFactoringProfile } from "store/user/selector";
import { useBrokerContext } from "context/BrokerContext";
import Select, { IProps as IPropSelect } from "../Select";
import { Control, Controller, FieldValues, FieldPath } from "react-hook-form";
import { IPaymentSpeed } from "../../models/HaulPay";
import type { IOption as SelectOption } from "../../models/Form";

interface PaymentSpeedOption extends SelectOption {
  id: number;
  payment_terms_id: string;
  value: string;
  text: string;
  label: string;
  is_default: boolean;
  rate: number;
  splitFee: number;
  discountRate: number;
}

export interface PaymentSpeedInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any
> {
  control?: Control<TFieldValues, TContext>;
  name: FieldPath<TFieldValues>;
  customOnChange?: (value: PaymentSpeedOption) => void;
  inputProps?: Partial<IPropSelect>;
  defaultPaymentSpeed?: IPaymentSpeed[];
  skip?: boolean;
  noLabel?: boolean;
  label?: string;
  factoringId?: string;
  direction?: "down" | "up";
}

export default function PaymentSpeedSelect<TFieldValues extends FieldValues>({
  control,
  name,
  customOnChange,
  inputProps,
  defaultPaymentSpeed,
  skip,
  noLabel,
  label,
  factoringId,
  direction = "down"
}: PaymentSpeedInputProps<TFieldValues>) {
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  const factoringProfile = useSelector(selectFactoringProfile);
  const {
    getDefaultPaymentSettings,
    defaultPaymentSettings,
    getBrokerDefaultPaymentSettings,
    paymentSettingsContextKey
  } = useBrokerContext();

  const requestedPaymentSettingsKey = useMemo(() => {
    if (factoringId) {
      return `broker:${factoringId}`;
    }

    const profileId = factoringProfile?.id;
    if (profileId) {
      return `default:${profileId}`;
    }

    return null;
  }, [factoringId, factoringProfile?.id]);

  useEffect(() => {
    // Skip fetching entirely if skip prop is true
    if (skip) {
      return;
    }

    // If keys already match, no fetch needed - ensure loading is false
    if (paymentSettingsContextKey === requestedPaymentSettingsKey) {
      setIsFetching(false);
      return;
    }

    const fetchSettings = async () => {
      setIsFetching(true);
      try {
        if (factoringId) {
          await getBrokerDefaultPaymentSettings(factoringId);
        } else {
          await getDefaultPaymentSettings(factoringProfile?.id);
        }
      } finally {
        setIsFetching(false);
        isFetchingRef.current = false;
      }
    };

    if (requestedPaymentSettingsKey && !isFetchingRef.current && !skip) {
      isFetchingRef.current = true;
      fetchSettings().catch(() => {
        // Error handled silently - settings fetch failure is non-critical
      });
    }
  }, [
    factoringId,
    factoringProfile?.id,
    getBrokerDefaultPaymentSettings,
    getDefaultPaymentSettings,
    paymentSettingsContextKey,
    requestedPaymentSettingsKey,
    skip
  ]);

  const formattedData: PaymentSpeedOption[] = useMemo(() => {
    return (
      (defaultPaymentSettings?.length && defaultPaymentSettings) ||
      (defaultPaymentSpeed?.length && defaultPaymentSpeed) ||
      []
    )
      .filter((term) => term.payee !== "factoring_company")
      .map((term, index) => {
        let label = term.payout_days === 0 ? "1 Day" : `${term.payout_days} Days`;
        if ((term.discount_rate || term.carrier_rate) === 0) {
          label += " (Free)";
        } else {
          label += ` (${term.discount_rate || term.carrier_rate}% Quick Pay Fee)`;
        }
        const value = term.payout_days === 0 ? "ach_1_day" : `ach_${term.payout_days}_day`;
        return {
          id: Number(term.id) || index + 1,
          payment_terms_id: term.id,
          value,
          text: label,
          label,
          is_default: term.is_default,
          rate: term.carrier_rate,
          splitFee: term.factoring_fee_split_rate,
          discountRate: term.discount_rate
        } as PaymentSpeedOption;
      });
  }, [defaultPaymentSettings, defaultPaymentSpeed]);

  const defaultValue =
    formattedData.find((option) => option.is_default) || formattedData[0] || null;

  if (!control) {
    return (
      <Select
        options={formattedData}
        label={
          (!noLabel && label && <div dangerouslySetInnerHTML={{ __html: label }} />) ??
          "Carrier Payment Speed"
        }
        className="flex-1"
        loading={isFetching}
        direction={direction}
        {...inputProps}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value = defaultValue, onChange } }) => {
        const paymentSpeedValue = formattedData.find((option) => option.value === value?.value);
        return (
          <Select
            options={formattedData}
            value={paymentSpeedValue}
            onChange={(selectedValue) => {
              const paymentSpeedOption = selectedValue as PaymentSpeedOption;
              if (customOnChange) {
                customOnChange(paymentSpeedOption);
              }
              onChange(paymentSpeedOption);
            }}
            label={
              (!noLabel && label && <div dangerouslySetInnerHTML={{ __html: label }} />) ??
              "Carrier Payment Speed"
            }
            className="flex-1 min-w-full"
            loading={isFetching}
            direction={direction}
            {...inputProps}
          />
        );
      }}
    />
  );
}
